// useResumeOnMount: cold-restart + context-change rehydration hook.
//
// R2 contract (handoff 2026-10-12 claude-runtime-r2-session-protocols):
//   The sales workspace calls this on mount and whenever a HubSpot
//   `hubspot.session` message brings a new customer context mid-session.
//   The server endpoint returns a single-shot snapshot:
//     { objective, approvedDecisions, outstandingSteps, pendingDrafts,
//       openAmbiguousOutcomes }
//   The UI uses the snapshot to prime its review screens without
//   re-prompting the rep for prior decisions.
//
// Hook shape:
//   const { status, data, error, refetch } = useResumeOnMount({ api, projectId });
//
//   status: 'idle' | 'loading' | 'ready' | 'error'
//   data:   ResumeState | null
//   error:  { message: string } | null
//   refetch: () => void       Imperatively re-request (used by the UI
//                             when the user hits a manual refresh or
//                             the connection comes back online).
//
// When projectId changes, the hook triggers a fresh fetch WITHOUT
// unmounting the app. That is how a HubSpot context switch mid-session
// flows through to the review screens.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ResumeStateApi, ResumeState } from '../api/resume-state.js';

export type ResumeStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseResumeOnMountArgs {
  api: ResumeStateApi | null;
  projectId: string | null;
  // Optional: notify the parent when a new snapshot lands.
  onSnapshot?: (state: ResumeState) => void;
  // Optional: notify the parent when the fetch fails.
  onError?: (err: Error) => void;
}

export interface UseResumeOnMountResult {
  status: ResumeStatus;
  data: ResumeState | null;
  error: { message: string } | null;
  refetch: () => void;
}

export function useResumeOnMount(args: UseResumeOnMountArgs): UseResumeOnMountResult {
  const { api, projectId, onSnapshot, onError } = args;
  const [status, setStatus] = useState<ResumeStatus>('idle');
  const [data, setData] = useState<ResumeState | null>(null);
  const [error, setError] = useState<{ message: string } | null>(null);
  // Keep callbacks in a ref so the fetch effect does not re-trigger on
  // every parent render that re-creates the handler.
  const onSnapshotRef = useRef(onSnapshot);
  const onErrorRef = useRef(onError);
  onSnapshotRef.current = onSnapshot;
  onErrorRef.current = onError;

  const run = useCallback(async () => {
    if (!api || !projectId) {
      setStatus('idle');
      setData(null);
      setError(null);
      return;
    }
    setStatus('loading');
    setError(null);
    try {
      const snapshot = await api.fetch(projectId);
      // Guard: if the hook got torn down or the projectId moved on while
      // we were awaiting, drop this result. The next effect run will
      // own the state transition.
      if (currentProjectRef.current !== projectId) return;
      setData(snapshot);
      setStatus('ready');
      onSnapshotRef.current?.(snapshot);
    } catch (err) {
      if (currentProjectRef.current !== projectId) return;
      const message = err instanceof Error ? err.message : 'Unknown failure';
      setData(null);
      setError({ message });
      setStatus('error');
      if (err instanceof Error) onErrorRef.current?.(err);
    }
  }, [api, projectId]);

  const currentProjectRef = useRef(projectId);
  currentProjectRef.current = projectId;

  useEffect(() => {
    void run();
  }, [run]);

  const refetch = useCallback(() => {
    void run();
  }, [run]);

  return { status, data, error, refetch };
}
